import { Header } from "./shared";
import Drip from "./Drip";
import DripCalendar from "./DripCalendar";
import Production from "./Production";
import Adverts from "./Adverts";
import EditorsLetter from "./EditorsLetter";
import { Eye } from "lucide-react";
import { PREVIEW_KEY } from "@/lib/glossyApi";
import { cycleIndexFor, editionLabel, editionStartIso } from "@/lib/issue";
import { useEffect, useState } from "react";
import client from "@/lib/glossyApi";
import { toast } from "sonner";

const BANNER_SWATCHES = [
  ["gold", "#c9a227", "Gold (house)"],
  ["ink", "#141414", "Ink"],
  ["burgundy", "#5e1b25", "Burgundy"],
  ["forest", "#1d3a2a", "Forest"],
  ["cream", "#f3ead9", "Cream"],
  ["pink", "#E8438A", "Pink & Black"],
  ["ember", "#E8641B", "Ember Orange & Ink"],
];

const EditionSwitchCard = () => {
  const [ed, setEd] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => client.get("/edition").then((r) => setEd(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const act = async (action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const r = await client.post("/admin/edition-mode", { action });
      setEd(r.data);
      try { localStorage.setItem("sme_edition_override", r.data.override === null ? "" : String(r.data.override)); } catch {}
      toast.success(action === "auto" ? "Back on the calendar — editions flip on the 1st & 15th." : action === "hold" ? "Held — nothing changes edition until YOU switch it." : "Switched — the whole magazine is now on the new edition.");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't do that."); }
    setBusy(false);
  };
  if (!ed) return null;
  const manual = ed.mode === "manual";
  return (
    <div className={`border p-5 mt-6 ${manual ? "border-gloss-gold bg-ink text-cream" : "border-ink/15 bg-white"}`} data-testid="edition-switch-card">
      <p className="overline text-gloss-gold">The edition switch</p>
      <p className={`font-serif text-xl mt-0.5 ${manual ? "text-cream" : ""}`}>Currently: {editionLabel(ed.index)} {manual ? "· UNDER YOUR CONTROL" : "· on the calendar"}</p>
      <p className={`font-sans text-xs mt-1 mb-4 ${manual ? "text-cream/60" : "text-ink/55"}`}>
        {manual
          ? "You hold the switch. The calendar can NOT flip the edition — nothing changes until you press Switch, or hand back to the calendar."
          : "The calendar flips editions automatically on the 1st & 15th. If you're working on articles manually, press Hold so nothing changes without your say-so."}
      </p>
      <div className="flex flex-wrap gap-3">
        {!manual && (
          <button onClick={() => act("hold")} disabled={busy} className="border border-ink px-5 py-2.5 overline text-xs hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="edition-hold-btn">
            Hold this edition — I'm working on it
          </button>
        )}
        <button onClick={() => act("next", `Switch the whole magazine to ${editionLabel(ed.index + 1)} right now?\n\nBanner, issue number and the drip calendar all move together. The calendar stays out of it until you hand control back.`)} disabled={busy}
          className="bg-gloss-gold text-ink px-5 py-2.5 overline text-xs hover:bg-cream transition-colors disabled:opacity-60" data-testid="edition-next-btn">
          Switch to {editionLabel(ed.index + 1).split(" · ")[0]} now
        </button>
        {manual && (
          <button onClick={() => act("auto", "Hand the switch back to the calendar? It will jump to whatever edition today's date says (1st & 15th rhythm).")} disabled={busy}
            className="border border-cream/40 text-cream px-5 py-2.5 overline text-xs hover:border-cream transition-colors disabled:opacity-60" data-testid="edition-auto-btn">
            Hand back to the calendar
          </button>
        )}
      </div>
    </div>
  );
};

const HomeBannerCard = () => {
  const [hb, setHb] = useState({ title: "", sub: "", color: "gold" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    client.get("/admin/settings").then((r) => setHb({ title: "", sub: "", color: "gold", ...(r.data.home_banner || {}) })).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try { await client.patch("/admin/settings", { home_banner: hb }); toast.success("Banner saved — live on the homepage."); }
    catch { toast.error("Couldn't save."); }
    setBusy(false);
  };
  return (
    <div className="border border-ink/15 bg-white p-5 mt-6" data-testid="home-banner-card">
      <p className="overline text-gloss-gold">Homepage banner</p>
      <p className="font-sans text-xs text-ink/55 mt-1 mb-4">The coloured strip at the top of the homepage. Leave the wording blank and it names the edition automatically ("The Second Edition" and so on) as editions switch.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input value={hb.title} onChange={(e) => setHb({ ...hb, title: e.target.value })} placeholder="Big line — blank = automatic edition name" className="border border-ink/25 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-ink" data-testid="banner-title-input" />
        <input value={hb.sub} onChange={(e) => setHb({ ...hb, sub: e.target.value })} placeholder="Small line — blank = house welcome line" className="border border-ink/25 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-ink" data-testid="banner-sub-input" />
      </div>
      <div className="flex items-center gap-2 mt-3">
        <p className="overline text-[10px] text-ink/60 mr-1">Colour</p>
        {BANNER_SWATCHES.map(([key, hex, label]) => (
          <button key={key} onClick={() => setHb({ ...hb, color: key })} title={label}
            className={`w-8 h-8 border-2 transition-transform ${hb.color === key ? "border-gloss-gold scale-110" : "border-ink/20 hover:scale-105"}`}
            style={{ background: hex }} data-testid={`banner-colour-${key}`} />
        ))}
        <button onClick={save} disabled={busy} className="ml-auto bg-ink text-cream px-5 py-2.5 overline text-xs hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="banner-save-btn">
          {busy ? "Saving…" : "Save banner"}
        </button>
      </div>
    </div>
  );
};

const SECTIONS_NAV = [
  ["editions-engine", "Engine & Switch"],
  ["editions-letter", "Editor's Letter"],
  ["editions-calendar", "Drip Calendar"],
  ["editions-production", "Issue Production"],
  ["editions-adplan", "Ad Plan"],
];

const SectionBand = ({ label, note }) => (
  <div className="bg-ink text-cream px-6 sm:px-10 py-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
    <span className="overline text-gloss-gold">{label}</span>
    {note && <span className="font-mono text-[11px] text-cream/50">{note}</span>}
  </div>
);

export default function Editions() {
  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const previewNext = () => {
    const nextIdx = cycleIndexFor() + 1;
    try { localStorage.setItem(PREVIEW_KEY, editionStartIso(nextIdx)); } catch { /* noop */ }
    window.open("/", "_blank");
  };
  return (
    <div data-testid="editions-tab">
      <Header kicker="What goes out, and when" title="Editions">
        <div className="flex flex-wrap items-center gap-1.5" data-testid="editions-nav">
          <button onClick={previewNext} title={`See the whole site exactly as it will look for ${editionLabel(cycleIndexFor() + 1)} — scheduled pieces and pinned adverts in place. Only you can see it.`} className="overline whitespace-nowrap flex items-center gap-2 px-3 py-1.5 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors" data-testid="preview-next-edition">
            <Eye size={13} /> Preview next edition
          </button>
          {SECTIONS_NAV.map(([id, label]) => (
            <button key={id} onClick={() => jump(id)} className="overline whitespace-nowrap px-3 py-1.5 border border-ink/25 text-ink/70 hover:bg-ink hover:text-cream transition-colors" data-testid={`editions-nav-${id}`}>
              {label}
            </button>
          ))}
        </div>
      </Header>

      <section id="editions-engine" data-testid="editions-engine">
        <SectionBand label="Engine & Switch" note="Automation on/off · run a drop manually any time" />
        <div className="px-6 sm:px-10"><Drip embedded /><EditionSwitchCard /><HomeBannerCard /></div>
      </section>

      <section id="editions-letter" data-testid="editions-letter">
        <SectionBand label="Editor's Letter" note="Auto-written each edition · edit or rewrite any time" />
        <div className="px-6 sm:px-10"><EditorsLetter /></div>
      </section>

      <section id="editions-calendar" data-testid="editions-calendar">
        <SectionBand label="Drip Calendar" note="Every member piece, planned by edition" />
        <div className="px-6 sm:px-10"><DripCalendar embedded /></div>
      </section>

      <section id="editions-production" data-testid="editions-production">
        <SectionBand label="Issue Production" note="Fairness rotation · build the issue · every article's status" />
        <div className="px-6 sm:px-10"><Production embedded /></div>
      </section>

      <section id="editions-adplan" data-testid="editions-adplan">
        <SectionBand label="Ad Plan" note="16 slots per edition · book or pull any run · per-member control lives in their Hub" />
        <div className="px-6 sm:px-10"><Adverts embedded /></div>
      </section>
    </div>
  );
}
